import {
  CheckIcon,
  SectionLabel,
  SignInPrompt,
  WordsHappenButton,
  WordsHappenFormAlert,
} from '@words-happen/ui-components';
import type React from 'react';
import { useState } from 'react';
import { type AccountOption, type AccountSearchEntry, accountOptionTarget } from '../account-options';
import { DisplayNameEditor, type SaveDisplayName } from './display-name-editor';
import styles from './profile-panel.module.scss';

/** The Profile tab for a guest: a prompt to create an account or sign in. */
export interface ProfilePanelGuestAccount {
  kind: 'guest';
  onCreateAccount: () => void;
  onSignIn: () => void;
}

/** The Profile tab for a signed-in player. */
export interface ProfilePanelPlayerAccount {
  kind: 'player';
  /** `loading` until the profile arrives, `load-error` if it couldn't. */
  status: 'loading' | 'ready' | 'load-error';
  onRetryLoad: () => void;
  /** The confirmed in-game name, or null until the player chooses one. */
  displayName: string | null;
  /** Pre-fills the field while there is no confirmed name: the name on their account, which may be empty or invalid. */
  suggestedDisplayName: string | null;
  /** Read-only; hidden when null. */
  email: string | null;
  /** The identity app's password reset, for an account with a password. Hidden when undefined. */
  resetPasswordHref?: string;
  onSaveDisplayName: SaveDisplayName;
  onSignOut: () => void;
  signingOut?: boolean;
}

export interface ProfilePanelProps {
  account: ProfilePanelGuestAccount | ProfilePanelPlayerAccount;
  className?: string;
}

const SIGN_IN_SECTION = 'Sign-in';

/** The Profile tab's options for account search, all of which need an account. Each control takes its label from here. */
export const PROFILE_OPTIONS = {
  displayName: {
    id: 'display-name',
    tab: 'profile',
    section: 'Display name',
    label: 'Display name',
    synonyms: ['name', 'username', 'nickname', 'player name'],
    signInTo: 'choose your display name',
  },
  email: {
    id: 'email',
    tab: 'profile',
    section: SIGN_IN_SECTION,
    label: 'Email',
    synonyms: ['email address', 'e-mail'],
  },
  resetPassword: {
    id: 'reset-password',
    tab: 'profile',
    section: SIGN_IN_SECTION,
    label: 'Reset password',
    synonyms: ['change password', 'forgot password', 'password'],
  },
  signOut: {
    id: 'sign-out',
    tab: 'profile',
    section: SIGN_IN_SECTION,
    label: 'Sign out',
    synonyms: ['log out', 'logout', 'log off', 'sign off'],
  },
} as const satisfies Record<string, AccountOption>;

/**
 * The Profile tab's options as this player sees them. A guest can use none of them; a player's Email and Reset
 * password are there only when their account has them.
 */
export const profileSearchEntries = (account: ProfilePanelProps['account']): AccountSearchEntry[] => {
  if (account.kind === 'guest') {
    return Object.values(PROFILE_OPTIONS).map((option) => ({ option, usable: false }));
  }
  return [
    PROFILE_OPTIONS.displayName,
    ...(account.email ? [PROFILE_OPTIONS.email] : []),
    ...(account.resetPasswordHref ? [PROFILE_OPTIONS.resetPassword] : []),
    PROFILE_OPTIONS.signOut,
  ].map((option) => ({ option, usable: true }));
};

const GuestProfile: React.FC<{ account: ProfilePanelGuestAccount }> = ({ account }) => (
  <section className={styles.section} aria-labelledby="profile-guest">
    <SectionLabel as="h2">
      <span id="profile-guest">Your profile</span>
    </SectionLabel>
    <p className={styles.text}>Create a free account to choose the name other players see and keep your progress.</p>
    <div className={styles.actions}>
      <WordsHappenButton variant="primary" onClick={account.onCreateAccount}>
        Create free account
      </WordsHappenButton>
      <SignInPrompt className={styles.signIn} onAction={account.onSignIn} />
    </div>
  </section>
);

const DisplayNameSection: React.FC<{ account: ProfilePanelPlayerAccount }> = ({ account }) => {
  const { displayName } = account;
  const [editing, setEditing] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const choosing = displayName === null;
  const open = choosing || editing;

  return (
    <section
      className={styles.section}
      aria-labelledby="profile-display-name"
      {...accountOptionTarget(PROFILE_OPTIONS.displayName)}
    >
      <SectionLabel as="h2">
        <span id="profile-display-name">
          {choosing ? 'Choose your display name' : PROFILE_OPTIONS.displayName.label}
        </span>
      </SectionLabel>
      {choosing ? (
        <p className={styles.text}>This is the name other players will see. You can change it later.</p>
      ) : null}
      {open ? (
        <DisplayNameEditor
          initialValue={displayName ?? account.suggestedDisplayName ?? ''}
          onSave={account.onSaveDisplayName}
          onSaved={() => {
            setEditing(false);
            setJustSaved(true);
          }}
          onCancel={choosing ? undefined : () => setEditing(false)}
          saveLabel={choosing ? 'Save name' : 'Save'}
          autoFocus={editing}
        />
      ) : (
        <div className={styles.nameRow}>
          <p className={styles.name}>{displayName}</p>
          <WordsHappenButton
            size="sm"
            aria-label="Change display name"
            onClick={() => {
              setJustSaved(false);
              setEditing(true);
            }}
          >
            Change
          </WordsHappenButton>
        </div>
      )}
      <p className={styles.saved} role="status">
        {justSaved && !open ? (
          <>
            <CheckIcon size={16} aria-hidden="true" /> Saved
          </>
        ) : null}
      </p>
    </section>
  );
};

const AccountDetails: React.FC<{ account: ProfilePanelPlayerAccount }> = ({ account }) =>
  account.email || account.resetPasswordHref ? (
    <section className={styles.section} aria-labelledby="profile-sign-in">
      <SectionLabel as="h2">
        <span id="profile-sign-in">{SIGN_IN_SECTION}</span>
      </SectionLabel>
      {account.email ? (
        // Focusable only from search, so it can take the player to their email.
        <dl className={styles.details} tabIndex={-1} {...accountOptionTarget(PROFILE_OPTIONS.email)}>
          <dt className={styles.detailLabel}>{PROFILE_OPTIONS.email.label}</dt>
          <dd className={styles.detailValue}>{account.email}</dd>
        </dl>
      ) : null}
      {account.resetPasswordHref ? (
        <a
          className={styles.link}
          href={account.resetPasswordHref}
          {...accountOptionTarget(PROFILE_OPTIONS.resetPassword)}
        >
          {PROFILE_OPTIONS.resetPassword.label}
        </a>
      ) : null}
    </section>
  ) : null;

const LoadState: React.FC<{ account: ProfilePanelPlayerAccount }> = ({ account }) =>
  account.status === 'loading' ? (
    <p className={styles.loading} role="status">
      Loading your profile…
    </p>
  ) : (
    // WordsHappenFormAlert is role="alert" (implicitly aria-live), so the error is read out as it appears.
    <div className={styles.error}>
      <WordsHappenFormAlert title="Couldn't load your profile">
        Check your connection and try again.
      </WordsHappenFormAlert>
      <WordsHappenButton size="sm" onClick={account.onRetryLoad}>
        Try again
      </WordsHappenButton>
    </div>
  );

const PlayerProfile: React.FC<{ account: ProfilePanelPlayerAccount }> = ({ account }) => (
  <>
    {account.status === 'ready' ? (
      <>
        <DisplayNameSection account={account} />
        <AccountDetails account={account} />
      </>
    ) : (
      <LoadState account={account} />
    )}
    <div className={styles.footer}>
      <WordsHappenButton
        variant="ink"
        loading={account.signingOut}
        disabled={account.signingOut}
        onClick={account.onSignOut}
        {...accountOptionTarget(PROFILE_OPTIONS.signOut)}
      >
        {PROFILE_OPTIONS.signOut.label}
      </WordsHappenButton>
      <div className={styles.deleteAccount}>
        <WordsHappenButton size="sm" disabled aria-describedby="profile-delete-soon">
          Delete account
        </WordsHappenButton>
        <span id="profile-delete-soon" className={styles.comingSoon}>
          Coming soon
        </span>
      </div>
    </div>
  </>
);

/**
 * The Profile tab of the account screen (/profile). A signed-in player sees and changes their in-game display name,
 * sees their email, resets their password (when their account has one), signs out, and sees where account deletion
 * will go. With no confirmed name, the field is open under "Choose your display name", pre-filled with the suggestion.
 * Guests get a prompt to create an account or sign in.
 */
export const ProfilePanel: React.FC<ProfilePanelProps> = ({ account, className }) => (
  <div className={[styles.panel, className].filter(Boolean).join(' ')}>
    {account.kind === 'guest' ? <GuestProfile account={account} /> : <PlayerProfile account={account} />}
  </div>
);
